import * as React from "react";
import { Trans } from "@lingui/react/macro";
import { Check, X } from "lucide-react";
import { AuthField } from "@app/ui/features/auth/AuthField";
import { useEmailFormatStatus } from "@app/ui/hooks/UseEmailFormatStatus";

export interface EmailFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** 검증 실패 문구(이미 번역된 상태). react-hook-form 을 쓰는 화면이 넘겨준다. */
  error?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * 로그인/가입 폼의 이메일 입력. 라벨/에러 문구 배치는 AuthField 를 그대로 쓰고,
 * 입력하는 동안 이메일 형식이 맞는지(완료/X)를 rightSlot 아이콘으로 즉석 표시한다.
 */
const EmailField = React.forwardRef<HTMLInputElement, EmailFieldProps>(
  ({ id = "email", value, onChange, error, onBlur, required, disabled, className }, ref) => {
    const status = useEmailFormatStatus(value);
    return (
      <AuthField
        ref={ref}
        id={id}
        name="email"
        type="email"
        autoComplete="email"
        label={<Trans>이메일</Trans>}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        // 이메일은 형식이 틀려도 문구 없이 빨간 테두리만 — 별도 안내가 필요 없다.
        hideErrorText
        rightSlot={
          status === "valid" ? (
            <Check
              key="valid"
              strokeWidth={2.5}
              className="size-4 text-emerald-500 duration-200 animate-in fade-in zoom-in-75"
            />
          ) : status === "invalid" ? (
            <X
              key="invalid"
              strokeWidth={2.5}
              className="size-4 text-destructive duration-200 animate-in fade-in zoom-in-75"
            />
          ) : undefined
        }
        required={required}
        disabled={disabled}
        className={className}
      />
    );
  },
);
EmailField.displayName = "EmailField";

export { EmailField };
